// src/components/presence/PresenceAvatars.tsx — SCE Issues
// Pile d'avatars des personnes connectées, à côté de la pastille du compte.
// Un clic liste qui, où, et depuis quand.

import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { PastillePersonne } from '@/components/personnes/PastillePersonne'
import { depuisQuand, libellePage, usePresence } from './PresenceProvider'

const MAX_AVATARS = 3
const FONDS = ['var(--color-gray-90)', 'var(--color-gray-70)', 'var(--color-gray-60)']

export function PresenceAvatars(): JSX.Element | null {
  const { presents } = usePresence()
  const location = useLocation()
  const [ouvert, setOuvert] = useState(false)
  const conteneur = useRef<HTMLDivElement>(null)

  // Fermeture au clic extérieur et à Échap — même règle que le menu du compte.
  useEffect(() => {
    if (!ouvert) return
    function auClic(e: MouseEvent): void {
      if (conteneur.current && !conteneur.current.contains(e.target as Node)) setOuvert(false)
    }
    function aLaTouche(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOuvert(false)
    }
    document.addEventListener('mousedown', auClic)
    document.addEventListener('keydown', aLaTouche)
    return () => {
      document.removeEventListener('mousedown', auClic)
      document.removeEventListener('keydown', aLaTouche)
    }
  }, [ouvert])

  if (presents.length === 0) return null

  const visibles = presents.slice(0, MAX_AVATARS)
  const reste = presents.length - visibles.length
  const n = presents.length

  return (
    <div ref={conteneur} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOuvert((o) => !o)}
        aria-expanded={ouvert}
        aria-label={`${n} personne${n > 1 ? 's' : ''} connectée${n > 1 ? 's' : ''} — voir qui`}
        className="flex items-center"
        style={{ position: 'relative', height: 44, padding: '0 4px', border: 'none', background: 'transparent', cursor: 'pointer' }}
      >
        {visibles.map((p, i) => (
          <PastillePersonne
            key={p.upn}
            nom={p.nom}
            upn={p.upn}
            fond={FONDS[i] ?? 'var(--color-gray-60)'}
            taillePolice={12}
            style={{ marginLeft: i === 0 ? 0 : -8, boxShadow: '0 0 0 2px var(--color-white)' }}
          />
        ))}
        {reste > 0 && (
          <span
            aria-hidden
            className="flex items-center justify-center"
            style={{
              width: 32,
              height: 32,
              marginLeft: -8,
              borderRadius: 16,
              backgroundColor: 'var(--color-white)',
              color: 'var(--color-black)',
              fontSize: 12,
              fontWeight: 600,
              boxShadow: '0 0 0 2px var(--color-white), inset 0 0 0 1px var(--color-gray-30)',
            }}
          >
            +{reste}
          </span>
        )}
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: 2,
            bottom: 6,
            width: 10,
            height: 10,
            borderRadius: 5,
            backgroundColor: 'var(--color-traffic-green)',
            boxShadow: '0 0 0 2px var(--color-white)',
          }}
        />
      </button>

      {ouvert && (
        <div
          role="dialog"
          aria-label="Connectés maintenant"
          style={{
            position: 'absolute',
            top: 48,
            right: 0,
            zIndex: 30,
            width: 300,
            padding: '8px 16px 14px',
            backgroundColor: 'var(--color-white)',
            border: '1px solid var(--color-border)',
            borderRadius: 12,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.16)',
            color: 'var(--color-black)',
          }}
        >
          <div className="flex items-center justify-between">
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Connectés maintenant</h2>
            <button
              type="button"
              onClick={() => setOuvert(false)}
              aria-label="Fermer"
              className="flex items-center justify-center"
              style={{ width: 44, height: 44, marginRight: -12, border: 'none', background: 'transparent' }}
            >
              <X size={18} />
            </button>
          </div>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {presents.map((p, i) => (
              <li key={p.upn} className="flex items-center gap-3" style={{ minHeight: 48 }}>
                <PastillePersonne nom={p.nom} upn={p.upn} fond={FONDS[i % FONDS.length]} taillePolice={12} />
                <span className="flex flex-col" style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{p.nom}</span>
                  <span style={{ fontSize: 12, color: 'var(--color-gray-70)' }}>
                    {p.page === location.pathname ? 'Sur cette page' : libellePage(p.page)} · {depuisQuand(p.derniereActivite)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
